/**
 * SEO utilities — JSON-LD schemas, meta tag helpers, sitemap generation.
 * Optimized for both traditional SEO and GEO (Generative Engine Optimization).
 */

import type { BlogPost, BlogFAQ } from '../types/blog';

const SITE_URL = 'https://goodatoms.com';
const SITE_NAME = 'Good Atoms';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

// ─── Organization Schema (site-wide) ───────────────────────────────────────
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description: 'STEAM-leksjoner utviklet i Verkstedet — vårt redaksjonelle lag — for nordiske barn 4–16 år. Undring som starter med spørsmål.',
    email: 'hei@goodatoms.com',
    sameAs: [
      'https://www.facebook.com/goodatoms/',
      'https://www.instagram.com/madeofgoodatoms/',
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Oslo',
      addressCountry: 'NO',
    },
  };
}

// ─── WebSite Schema (site-wide) ────────────────────────────────────────────
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/undring?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

// ─── Article Schema (per blog post) ────────────────────────────────────────
export function articleSchema(post: BlogPost) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    image: post.image || DEFAULT_OG_IMAGE,
    author: {
      '@type': 'Person',
      name: post.author,
      jobTitle: post.authorTitle || undefined,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/favicon.svg`,
      },
    },
    datePublished: post.date,
    dateModified: post.updatedAt || post.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/undring/${post.slug}`,
    },
    inLanguage: post.lang === 'no' ? 'nb-NO' : 'en',
    keywords: [post.primaryKeyword, ...(post.secondaryKeywords || []), ...post.tags].join(', '),
    wordCount: post.content.split(/\s+/).length,
  };
}

// ─── FAQ Schema (per blog post, critical for GEO) ──────────────────────────
export function faqSchema(faqs: BlogFAQ[]) {
  if (!faqs.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// ─── BreadcrumbList Schema ─────────────────────────────────────────────────
export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ─── Product Schema (app listing) ──────────────────────────────────────────
export function productSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    url: SITE_URL,
    description: 'STEAM-læring for barn i alderen 4–16. Interaktive leksjoner som begynner med undring.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'NOK',
      description: 'Gratis plan tilgjengelig',
    },
    aggregateRating: undefined, // Add when we have ratings
  };
}

// ─── Meta tag builder ──────────────────────────────────────────────────────
export interface PageMeta {
  title: string;
  description: string;
  url: string;
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  locale?: string;
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
  /** hreflang alternate-language links (e.g. nb-NO / en / x-default) */
  alternates?: { hreflang: string; href: string }[];
}

export function buildPageMeta(meta: PageMeta) {
  return {
    title: `${meta.title} | ${SITE_NAME}`,
    description: meta.description,
    canonical: meta.url,
    openGraph: {
      title: meta.title,
      description: meta.description,
      url: meta.url,
      siteName: SITE_NAME,
      image: meta.image || DEFAULT_OG_IMAGE,
      imageAlt: meta.imageAlt || meta.title,
      type: meta.type || 'website',
      locale: meta.locale || 'nb_NO',
      ...(meta.publishedTime && { 'article:published_time': meta.publishedTime }),
      ...(meta.modifiedTime && { 'article:modified_time': meta.modifiedTime }),
      ...(meta.author && { 'article:author': meta.author }),
      ...(meta.section && { 'article:section': meta.section }),
      ...(meta.tags && { 'article:tag': meta.tags }),
    },
    twitter: {
      card: 'summary_large_image',
      title: meta.title,
      description: meta.description,
      image: meta.image || DEFAULT_OG_IMAGE,
    },
  };
}

// ─── URL helpers ───────────────────────────────────────────────────────────
export function blogUrl(slug: string) {
  return `${SITE_URL}/undring/${slug}`;
}

export function siteUrl(path = '') {
  return `${SITE_URL}${path}`;
}
